import { useNavigate } from 'react-router-dom'
import { useOrg } from '@/context/OrgContext'
import { useAuth } from '@/context/AuthContext'
import { Button } from '@/components/ui/button'
import { OrgLogo } from '@/components/OrgLogo'
import { LogOut, Shield, ChevronRight } from 'lucide-react'

export default function OrgPickerPage() {
  const { memberships, setActiveOrgId, isPlatformAdmin } = useOrg()
  const { signOut } = useAuth()
  const navigate = useNavigate()

  function pick(orgId: string) {
    setActiveOrgId(orgId)
    navigate('/', { replace: true })
  }

  return (
    <div className="min-h-[100dvh] bg-background flex flex-col">
      <header className="flex items-center justify-between px-4 pt-safe h-14">
        <p className="text-base font-bold text-foreground">TourFlow</p>
        <button
          onClick={signOut}
          className="text-muted-foreground hover:text-foreground transition-colors p-2.5"
          aria-label="Sign out"
        >
          <LogOut className="w-5 h-5" />
        </button>
      </header>

      <main className="flex-1 px-4 pt-16 pb-8 max-w-md mx-auto w-full">
        <div className="w-full">
          {/* Header fades in with the page */}
          <div className="mb-6 animate-in fade-in dur-base ease-out-soft">
            <h1 className="text-2xl font-semibold text-foreground">Choose an organization</h1>
            <p className="text-sm text-muted-foreground mt-2">
              {pickerSubtitle(memberships.length, isPlatformAdmin)}
            </p>
          </div>

          {/* Cards cascade down from a shared starting zone — like a deck
              fanning open. Each card uses slide-in-from-top + fill-mode
              backwards + a staggered delay so they appear to emerge from
              where the first card settles, not from their final positions. */}
          <div className="space-y-2">
          {memberships.map((m, i) => (
            <button
              key={m.org_id}
              onClick={() => pick(m.org_id)}
              style={{ animationDelay: `${i * 70}ms` }}
              className="w-full text-left rounded-xl border border-border bg-card p-5 flex items-center gap-4 shadow-sm hover:bg-accent hover:border-foreground/20 active:scale-[0.99] transition-all dur-fast animate-in fade-in slide-in-from-top-8 fill-mode-backwards dur-base ease-out-soft"
            >
              <OrgLogo
                url={m.organization.logo_url}
                name={m.organization.nombre}
                variant={m.organization.logo_variant}
                className="w-11 h-11 rounded-lg text-base"
              />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-base text-foreground truncate">{m.organization.nombre}</p>
                <p className="text-sm text-muted-foreground mt-0.5">{roleLabel(m.rol_nivel)}</p>
              </div>
              <ChevronRight className="w-5 h-5 text-primary shrink-0" />
            </button>
          ))}

          {isPlatformAdmin && (
            <button
              onClick={() => navigate('/admin/orgs')}
              style={{ animationDelay: `${memberships.length * 70}ms` }}
              className="w-full text-left rounded-xl border border-border bg-card p-5 flex items-center gap-4 shadow-sm hover:bg-accent hover:border-foreground/20 active:scale-[0.99] transition-all dur-fast animate-in fade-in slide-in-from-top-8 fill-mode-backwards dur-base ease-out-soft"
            >
              <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <Shield className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-base text-foreground">Platform admin</p>
                <p className="text-sm text-muted-foreground mt-0.5">Manage organizations</p>
              </div>
              <ChevronRight className="w-5 h-5 text-primary shrink-0" />
            </button>
          )}
        </div>

          {memberships.length === 0 && (
            <div className="text-center py-10 animate-in fade-in dur-base ease-out-soft">
              <p className="text-sm text-muted-foreground mb-4">
                You aren't part of any organization yet. Ask your admin to add you.
              </p>
              <Button variant="outline" size="sm" onClick={signOut}>
                Sign out
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}


function pickerSubtitle(orgCount: number, isAdmin: boolean): string {
  if (orgCount === 0) return 'Pick where to continue.'
  const orgLabel = orgCount === 1 ? '1 organization' : `${orgCount} organizations`
  if (isAdmin) return `You belong to ${orgLabel} and have admin access. Pick where to continue.`
  return `You belong to ${orgLabel}. Pick one to continue.`
}

function roleLabel(rol: 'A' | 'B' | 'C') {
  if (rol === 'A') return 'Manager'
  if (rol === 'B') return 'Artist'
  return 'Crew'
}
